/**
 * Watched-state store. Persisted to localStorage in the exact flat,
 * Codable-shaped format from packages/schema/persist.ts — see
 * ./persistedState.ts for how this store shares that single unified record
 * with the settings store without either clobbering the other's slice.
 *
 * The store keeps full `WatchRecord`s (so richer data — ratings, rewatches,
 * a letterboxd-import source — survives round trips even though nothing in
 * the UI sets those fields yet) but exposes the narrower `watched: Record<EntryId,
 * ISODate>` shape components already consume, derived from those records.
 */

import type { EntryId, ISODate } from "@schema/catalog.ts";
import type { Tombstones, WatchRecord } from "@schema/persist.ts";
import { create } from "zustand";
import { type PersistStorage, persist } from "zustand/middleware";
import { readPersistedState, writeWatched } from "./persistedState.ts";

interface ProgressState {
  /** Derived view: entry id → the day it was watched. What components read. */
  watched: Record<EntryId, ISODate>;
  /** Full records — the source of truth serialized to disk. */
  records: Record<EntryId, WatchRecord>;
  /**
   * Un-watches, with when they happened.
   *
   * Only sync reads these, and only to stop a deliberate un-tick being undone
   * by a device that still has the entry — see lib/sync/merge.ts rule 5. Every
   * action that marks something watched must clear its tombstone, or the next
   * merge will helpfully delete it again.
   */
  deleted: Tombstones;
  toggleWatched: (id: EntryId) => void;
  /**
   * Your own score for something you've watched, on Letterboxd's 1-10
   * half-star scale (`null` clears it). Stored on the watch record rather than
   * anywhere new, because that is the field the Letterboxd import and export
   * already round-trip — a rating set here exports, and one imported from
   * Letterboxd shows up here.
   */
  setRating: (id: EntryId, rating10: number | null) => void;
  markAllWatched: (ids: EntryId[]) => void;
  /**
   * Bulk-applies a Letterboxd import. "additive" merges `records` into the
   * existing set (import wins on a shared entry, untouched entries survive);
   * "replace" makes the watched set exactly `records`, dropping anything not
   * in the import — see the diff preview in ImportDialog, which is what
   * decides which mode the user actually meant.
   */
  applyImport: (records: Record<EntryId, WatchRecord>, mode: "additive" | "replace") => void;
  /** Applies a merged sync result verbatim — see the note on the implementation. */
  applyMerged: (records: Record<EntryId, WatchRecord>, deleted: Tombstones) => void;
  clearProgress: () => void;
}

type PersistedProgress = Pick<ProgressState, "watched" | "records" | "deleted">;

// Local calendar date, not UTC — "watched tonight" must not roll to tomorrow.
const todayISO = (): ISODate => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
};

const deriveWatched = (records: Record<EntryId, WatchRecord>): Record<EntryId, ISODate> => {
  const watched: Record<EntryId, ISODate> = {};
  for (const [id, record] of Object.entries(records)) watched[id] = record.watchedAt;
  return watched;
};

const storage: PersistStorage<PersistedProgress> = {
  getItem: (_name) => {
    const stored = readPersistedState();
    return {
      state: {
        records: stored.watched,
        deleted: stored.deleted,
        watched: deriveWatched(stored.watched),
      },
      version: 1,
    };
  },
  setItem: (_name, value) => {
    writeWatched(value.state.records, value.state.deleted);
  },
  removeItem: () => {
    writeWatched({}, {});
  },
};

export const useProgressStore = create<ProgressState>()(
  persist(
    (set, get) => ({
      watched: {},
      records: {},
      deleted: {},
      setRating: (id, rating10) => {
        const records = { ...get().records };
        const deleted = { ...get().deleted };
        const existing = records[id];
        // Rating something implies having watched it. Silently marking it
        // watched is friendlier than refusing the click, and matches what the
        // gesture obviously means.
        records[id] = existing
          ? { ...existing, rating10 }
          : { watchedAt: todayISO(), rating10, rewatch: false, source: "manual" };
        // Rating implies watching, so any un-watch this contradicts is spent.
        delete deleted[id];
        set({ records, deleted, watched: deriveWatched(records) });
      },
      toggleWatched: (id) => {
        const records = { ...get().records };
        const deleted = { ...get().deleted };
        if (records[id]) {
          delete records[id];
          // The tombstone is the whole point: without it the next sync sees an
          // absence, cannot tell it from "never knew about this", and puts the
          // entry back.
          deleted[id] = Date.now();
        } else {
          records[id] = { watchedAt: todayISO(), rating10: null, rewatch: false, source: "manual" };
          // Re-ticking retires the un-watch. Leaving it would make merge delete
          // this again on the next sync, since the tombstone is newer than the
          // day just recorded.
          delete deleted[id];
        }
        set({ records, deleted, watched: deriveWatched(records) });
      },
      markAllWatched: (ids) => {
        const records = { ...get().records };
        const deleted = { ...get().deleted };
        const today = todayISO();
        for (const id of ids) {
          records[id] ??= { watchedAt: today, rating10: null, rewatch: false, source: "manual" };
          delete deleted[id];
        }
        set({ records, deleted, watched: deriveWatched(records) });
      },
      applyImport: (imported, mode) => {
        const records = mode === "replace" ? { ...imported } : { ...get().records, ...imported };
        const deleted = { ...get().deleted };
        // Anything the import says was watched is no longer un-watched.
        for (const id of Object.keys(imported)) delete deleted[id];
        // "replace" drops entries the import omits. Those are deliberate
        // removals — the diff preview showed them — so they leave tombstones
        // like any other un-watch.
        if (mode === "replace") {
          const now = Date.now();
          for (const id of Object.keys(get().records)) {
            if (!(id in imported)) deleted[id] = now;
          }
        }
        set({ records, deleted, watched: deriveWatched(records) });
      },
      /** Applies an already-merged sync result. Records no new un-watches:
       *  merge decided what the deletions are, and re-deriving them here would
       *  tombstone every entry this device simply had not heard of yet. */
      applyMerged: (records, deleted) =>
        set({ records: { ...records }, deleted: { ...deleted }, watched: deriveWatched(records) }),
      clearProgress: () => {
        const now = Date.now();
        const deleted = { ...get().deleted };
        for (const id of Object.keys(get().records)) deleted[id] = now;
        set({ records: {}, deleted, watched: {} });
      },
    }),
    {
      name: "progress", // unused as a real localStorage key — storage reads/writes the unified record directly
      version: 1,
      storage,
      partialize: (state) => ({
        watched: state.watched,
        records: state.records,
        deleted: state.deleted,
      }),
    },
  ),
);
